import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SIZE } from '../../../../../../constants/size'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../../../../../constants/validation'
import { ConfirmationModal } from '../../../../../Confirmation'
import { FormModal } from '../../../../../Form'
import { useApplicantProfileContext } from '../../../RentalPortalApplicantProfile.hooks'
import { getApplicantProfileTitle } from '../../../RentalPortalApplicantProfile.utils'
import {
  getApplicantCreditCheckVerdictResult,
  getHasApplicantCreditCheckDocument,
} from '../ApplicantCreditCheckBlock.utils'

import {
  APPLICANT_CREDIT_CHECK_VERDICT_DISCARD_CONFIRMATION_PROPS,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_CORRECTION,
  QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_SAVE,
} from './ApplicantCreditCheckVerdictModal.constants'
import {
  getApplicantCreditCheckVerdictInitialValues,
  getApplicantCreditCheckVerdictPayload,
  getApplicantCreditCheckVerdictValidationSchema,
} from './ApplicantCreditCheckVerdictModal.utils'
import { ApplicantCreditCheckVerdictModalFields } from './ApplicantCreditCheckVerdictModalFields'

function ApplicantCreditCheckVerdictModal({
  extraction,
  isCorrection,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation()

  const { applicantData } = useApplicantProfileContext()

  const [correction, setCorrection] = useState(null)

  const result = getApplicantCreditCheckVerdictResult(
    applicantData?.credit_check
  )

  const hasDocument = getHasApplicantCreditCheckDocument(
    applicantData?.credit_check
  )

  async function handleSaveVerdict(submitted) {
    try {
      await onSubmit({ isCorrection, values: submitted.value })
    } catch {
      setCorrection(null)

      submitted.setSubmitting(false)

      submitted.setErrors({
        [SERVER_NON_FIELD_ERROR_KEY]: t(
          'RentalPortal.ApplicantCreditCheckVerdictSaveErrorKey'
        ),
      })
    }
  }

  function handleSubmit(submitted) {
    return isCorrection
      ? setCorrection(submitted)
      : handleSaveVerdict(submitted)
  }

  function handleConfirmCorrection() {
    return handleSaveVerdict(correction)
  }

  function handleCancelCorrection() {
    correction.setSubmitting(false)

    setCorrection(null)
  }

  return (
    <>
      <FormModal
        Fields={ApplicantCreditCheckVerdictModalFields}
        containerProps={{
          boxSize: SIZE.LARGE,
          confirmationProps:
            APPLICANT_CREDIT_CHECK_VERDICT_DISCARD_CONFIRMATION_PROPS,
          qaName: QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT,
        }}
        formBodyProps={{
          actionsProps: {
            submitProps: {
              disabled: !hasDocument,
              tooltip: hasDocument
                ? null
                : t(
                    'RentalPortal.ApplicantCreditCheckVerdictNoDocumentTooltipKey'
                  ),
            },
          },
          contentProps: { size: SIZE.LARGE },
          submitButtonText:
            'RentalPortal.ApplicantCreditCheckVerdictSaveBtnKey',
          submitQaName: QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_SAVE,
          withNotifications: true,
        }}
        formFieldsProps={{ extraction, hasDocument, result }}
        formTitle={
          isCorrection
            ? 'RentalPortal.ApplicantCreditCheckVerdictCorrectionModalTitleKey'
            : 'RentalPortal.ApplicantCreditCheckVerdictModalTitleKey'
        }
        getPayload={getApplicantCreditCheckVerdictPayload}
        initialValues={getApplicantCreditCheckVerdictInitialValues({
          extraction,
          result,
        })}
        validationSchema={getApplicantCreditCheckVerdictValidationSchema(t)}
        onClose={onClose}
        onSubmit={handleSubmit}
      />

      {correction ? (
        <ConfirmationModal
          actionButtonsProps={{
            submitText: t('RentalPortal.ApplicantCreditCheckVerdictSaveBtnKey'),
          }}
          description={t(
            'RentalPortal.ApplicantCreditCheckVerdictCorrectionConfirmTextKey',
            {
              applicant: getApplicantProfileTitle({ applicantData, t }),
              provider: result?.provider_display_name,
            }
          )}
          qaName={QA_NAME_APPLICANT_CREDIT_CHECK_VERDICT_CORRECTION}
          title={t(
            'RentalPortal.ApplicantCreditCheckVerdictCorrectionConfirmTitleKey'
          )}
          onCancel={handleCancelCorrection}
          onSubmit={handleConfirmCorrection}
        />
      ) : null}
    </>
  )
}

export default ApplicantCreditCheckVerdictModal
